import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import OnboardingWizard from './OnboardingWizard';
import BetaBanner from '../BetaBanner';
import PageMeta from '../PageMeta';
import MobileBottomNav from './MobileBottomNav';
import {
  LayoutDashboard,
  Home,
  IndianRupee,
  Users,
  Building2,
  ListTodo,
  FileText,
  Store,
  Zap,
  Building,
  CreditCard,
  MessageCircle,
  UtensilsCrossed,
  Sparkle,
  Package,
  Megaphone,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Search,
  Bell,
  Wallet,
  UserCheck,
  FolderOpen
} from 'lucide-react';

const ManagerLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [profileName, setProfileName] = useState(user?.name || 'Admin');
  const [propsLoaded, setPropsLoaded] = useState(false);
  const onboardingKey = user?.id ? `onboarding_dismissed_${user.id}` : 'onboarding_dismissed';
  const [onboardingDone, setOnboardingDone] = useState(() => localStorage.getItem(onboardingKey) === 'true');

  // Fetch user profile on mount and update name
  React.useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.name) {
            setProfileName(data.name);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  // Fetch properties on mount
  React.useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProperties(data);
          // Initialize with all properties selected
          const savedSelection = localStorage.getItem('selectedProperties');
          if (savedSelection) {
            setSelectedProperties(JSON.parse(savedSelection));
          } else {
            const allIds = data.map(p => p.id);
            setSelectedProperties(allIds);
            localStorage.setItem('selectedProperties', JSON.stringify(allIds));
          }
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setPropsLoaded(true);
      }
    };
    fetchProperties();
  }, []);

  const refreshPropertiesAfterOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
        const allIds = data.map(p => p.id);
        setSelectedProperties(allIds);
        localStorage.setItem('selectedProperties', JSON.stringify(allIds));
      }
    } catch (e) { /* noop */ }
    localStorage.setItem(onboardingKey, 'true');
    setOnboardingDone(true);
  };

  // Toggle property selection
  const togglePropertySelection = (propertyId) => {
    setSelectedProperties(prev => {
      const newSelection = prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId];
      
      localStorage.setItem('selectedProperties', JSON.stringify(newSelection));
      return newSelection;
    });
  };

  // Select all properties
  const selectAllProperties = () => {
    const allIds = properties.map(p => p.id);
    setSelectedProperties(allIds);
    localStorage.setItem('selectedProperties', JSON.stringify(allIds));
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedProperties([]);
    localStorage.setItem('selectedProperties', JSON.stringify([]));
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyDropdown && !event.target.closest('.property-dropdown-container')) {
        setShowPropertyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPropertyDropdown]);

  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      path: '/manager/dashboard',
      color: 'text-blue-500',
      active: true
    },
    { 
      id: 'home', 
      label: 'Home', 
      icon: Home, 
      path: '/manager/home',
      color: 'text-gray-600',
      active: true
    },
    { 
      id: 'money', 
      label: 'Money', 
      icon: IndianRupee, 
      path: '/manager/money',
      color: 'text-green-500',
      active: true,
      submenu: [
        { label: 'Collections', path: '/manager/money/collections', active: true },
        { label: 'Dues', path: '/manager/money/dues', active: true },
        { label: 'Invoices & Receipts', path: '/manager/invoices', active: true },
        { label: 'Expenses', path: '/manager/money/expenses', active: true },
        { label: 'Banks & UPI', path: '/manager/money/banks', active: true },
        { label: 'Profit & Loss', path: '/manager/money/profit-loss', active: true },
        { label: 'Dues Packages', path: '/manager/money/dues-packages', active: true }
      ]
    },
    { 
      id: 'people', 
      label: 'People', 
      icon: Users, 
      path: '/manager/people',
      color: 'text-purple-500',
      active: true,
      submenu: [
        { label: 'Tenants', path: '/manager/people/tenants', active: true },
        { label: 'Leads', path: '/manager/people/leads', active: true },
        { label: 'Bookings', path: '/manager/people/bookings', active: true },
        { label: 'Old Tenants', path: '/manager/people/old-tenants', active: true },
        { label: 'Team', path: '/manager/people/team', active: true }
      ]
    },
    { 
      id: 'property', 
      label: 'Property', 
      icon: Building2, 
      path: '/manager/property/list',
      color: 'text-orange-500',
      active: true
    },
    { 
      id: 'tasks', 
      label: 'Tasks', 
      icon: ListTodo, 
      path: '/manager/tasks',
      badge: 'New',
      color: 'text-red-500',
      active: true
    },
    { 
      id: 'complaints', 
      label: 'Complaints', 
      icon: MessageCircle, 
      path: '/manager/complaints',
      color: 'text-red-600',
      active: true
    },
    { 
      id: 'notice-board', 
      label: 'Notice Board', 
      icon: Megaphone, 
      path: '/manager/notice-board',
      badge: 'New',
      color: 'text-purple-600',
      active: true
    },
    { 
      id: 'visitors', 
      label: 'Visitor Log', 
      icon: UserCheck, 
      path: '/manager/visitors',
      badge: 'New',
      color: 'text-indigo-600',
      active: true
    },
    { 
      id: 'food', 
      label: 'Food / Mess', 
      icon: UtensilsCrossed, 
      path: '/manager/food',
      color: 'text-orange-500',
      active: true
    },
    { 
      id: 'cleaning', 
      label: 'Cleaning', 
      icon: Sparkle, 
      path: '/manager/cleaning',
      color: 'text-cyan-500',
      active: true
    },
    { 
      id: 'assets', 
      label: 'Assets', 
      icon: Package, 
      path: '/manager/assets',
      color: 'text-amber-600',
      active: true
    },
    { 
      id: 'listings', 
      label: 'Listings', 
      icon: Store, 
      path: '/manager/listings',
      color: 'text-indigo-600',
      active: true
    },
    { 
      id: 'reports', 
      label: 'Reports', 
      icon: FileText, 
      path: '/manager/reports',
      color: 'text-indigo-500',
      active: true
    },
    { 
      id: 'agreements', 
      label: 'Rent Agreement', 
      icon: FileText, 
      path: '/manager/agreements',
      color: 'text-teal-500',
      active: true
    },
    { 
      id: 'documents', 
      label: 'Documents Vault', 
      icon: FolderOpen, 
      path: '/manager/documents',
      color: 'text-blue-600',
      active: true
    },
    { 
      id: 'police-verification', 
      label: 'Police Verification', 
      icon: FileText, 
      path: '/manager/police-verification',
      color: 'text-red-500',
      active: true
    },
    { 
      id: 'reminders', 
      label: 'Rent Reminders', 
      icon: Bell, 
      path: '/manager/reminders',
      color: 'text-yellow-500',
      active: true
    },
    { 
      id: 'electricity', 
      label: 'Electricity', 
      icon: Zap, 
      path: '/manager/electricity',
      badge: 'New',
      color: 'text-yellow-500',
      active: true
    },
    { 
      id: 'banks', 
      label: 'Banks', 
      icon: Building, 
      path: '/manager/banks',
      color: 'text-cyan-500',
      comingSoon: true
    },
    { 
      id: 'flexpe', 
      label: 'FlexPe', 
      icon: CreditCard, 
      path: '/manager/flexpe',
      color: 'text-teal-500',
      comingSoon: true
    },
    { 
      id: 'whatsapp', 
      label: 'Whatsapp', 
      icon: MessageCircle, 
      path: '/manager/whatsapp',
      color: 'text-green-600',
      comingSoon: true
    },
    { 
      id: 'wallet', 
      label: 'Wallet', 
      icon: Wallet, 
      path: '/manager/wallet',
      badge: 'New',
      color: 'text-blue-600',
      active: true
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings, 
      path: '/manager/settings',
      color: 'text-gray-600',
      active: true
    }
  ];

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/manager/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Page title map keyed by path segment → used by <PageMeta> below.
  const MANAGER_TITLES = {
    'home':                 ['Dashboard',          'Overview of properties, rent collection and tenant activity on RentKollect.'],
    'dashboard':            ['Dashboard',          'Real-time snapshot of your PG/hostel operations.'],
    'money':                ['Money',              'Rent, deposits, expenses and pending dues across all properties.'],
    'money/collections':    ['Collections',        'Every rent & deposit payment received this month.'],
    'money/dues':           ['Pending Dues',       'Outstanding rent from active tenants, with one-click WhatsApp reminders.'],
    'money/expenses':       ['Expenses',           'Property expenses by category, month and vendor.'],
    'money/banks':          ['Banks & UPI',        'Bank accounts and UPI VPAs used to receive rent — set primary per property.'],
    'money/profit-loss':    ['Profit & Loss',      'Revenue minus expenses for the selected period.'],
    'money/dues-packages':  ['Dues Packages',      'Bulk dues / package configuration.'],
    'people/tenants':       ['Tenants',            'Active tenant roster across properties.'],
    'people/leads':         ['Leads',              'Prospective tenants from listings and referrals.'],
    'people/bookings':      ['Bookings',           'Tenant bookings and reservation pipeline.'],
    'people/team':          ['Team',               'Manage your on-ground staff and their access.'],
    'people/old-tenants':   ['Past Tenants',       'Archive of vacated tenants.'],
    'complaints':           ['Complaints',         'Track tenant complaints and SLAs.'],
    'notice-board':         ['Notice Board',       'Broadcast announcements, rules and updates to tenants and staff.'],
    'visitors':             ['Visitor Log',        'Front-desk register for guests, deliveries, and workers.'],
    'wallet':               ['Wallet & Credits',   'Pre-paid credits for e-stamping and Aadhaar e-sign.'],
    'invoices':             ['Invoices & Receipts', 'Branded PDF bills and auto-generated receipts for every payment.'],
    'food':                 ['Food / Mess',        'Daily meal schedule and mess management.'],
    'cleaning':             ['Cleaning',           'Housekeeping checklists and attendance.'],
    'assets':               ['Assets',             'Inventory across rooms and common areas.'],
    'listings':             ['Listings',           'Publish vacant rooms to your public page.'],
    'property/list':        ['Properties',         'All PGs, hostels and apartments you manage.'],
    'property/rooms':       ['Rooms',              'Room-level occupancy and rent configuration.'],
    'tasks':                ['Tasks',              'Checklist of property operations and reminders.'],
    'reports':              ['Reports',            'Occupancy, collection %, P&L and aging reports.'],
    'agreements':           ['Rent Agreements',    'Digital e-rent agreements with auto-fill.'],
    'documents':            ['Documents Vault',    'KYC, FSSAI, fire NOC, agreements — all property and tenant docs in one secure place.'],
    'police-verification':  ['Police Verification','Tenant police verification records.'],
    'reminders':            ['Reminders',          'Automated rent reminders via WhatsApp and SMS.'],
    'reminders/settings':   ['Reminder Settings',  'Cadence, templates and channel config.'],
    'settings':             ['Settings',           'Account, notifications and Kolly preferences.'],
    'electricity':          ['Electricity',        'Track meter readings and electricity billing.'],
    'banks':                ['Banks',              'Bank accounts and UPI reconciliation.'],
    'flexpe':               ['FlexPe',             'Flexible payment collection for tenants.'],
    'whatsapp':             ['WhatsApp',           'Templates and automation for owner-tenant chat.'],
  };

  const pagePath = location.pathname.replace(/^\/manager\/?/, '').replace(/\/$/, '');
  // Match deepest prefix
  let titleKey = pagePath;
  if (!MANAGER_TITLES[titleKey]) {
    const parts = pagePath.split('/');
    while (parts.length > 0 && !MANAGER_TITLES[parts.join('/')]) parts.pop();
    titleKey = parts.join('/');
  }
  const [pageTitle, pageDescription] = MANAGER_TITLES[titleKey] || ['Owner Dashboard', 'Manage your PG/hostel on RentKollect.'];

  return (
    <div className="flex min-h-screen lg:h-screen bg-gray-50">
      <PageMeta title={pageTitle} description={pageDescription} noindex />
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-900 to-blue-950 text-white
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static
      `}>
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="p-6 border-b border-blue-800">
            <div className="flex items-center justify-between">
              <div className="bg-blue-600 rounded-xl px-4 py-2">
                <h1 className="font-bold text-lg">RentKollect</h1>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* User Info */}
            <div className="mt-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-bold">
                {profileName?.charAt(0) || 'A'}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Hi {profileName} 👋</p>
                <p className="text-xs text-blue-300">{user?.phone}</p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                const isExpanded = expandedMenus[item.id];
                const isComingSoon = item.comingSoon;

                return (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        if (isComingSoon) return; // Disable click for coming soon
                        
                        if (hasSubmenu) {
                          // Always navigate to the parent path when clicked
                          navigate(item.path);
                          setSidebarOpen(false);
                          // Also toggle submenu for desktop view
                          toggleSubmenu(item.id);
                        } else {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }
                      }}
                      title={isComingSoon ? "Coming Soon" : ""}
                      className={`
                        w-full flex items-center justify-between px-4 py-3 rounded-lg
                        transition-all duration-200 group relative
                        ${isComingSoon 
                          ? 'opacity-40 cursor-not-allowed' 
                          : active 
                            ? 'bg-blue-800 text-white shadow-lg' 
                            : 'hover:bg-blue-800/50 text-blue-100'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={20} className={active ? 'text-white' : item.color} />
                        <span className="font-medium text-sm">{item.label}</span>
                        {item.badge && (
                          <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full">
                            {item.badge}
                          </span>
                        )}
                        {isComingSoon && (
                          <span className="text-[9px] bg-yellow-500 text-white px-2 py-0.5 rounded-full font-semibold">
                            SOON
                          </span>
                        )}
                      </div>
                      {hasSubmenu && !isComingSoon && (
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      )}
                    </button>

                    {/* Submenu */}
                    {hasSubmenu && isExpanded && !isComingSoon && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subitem) => {
                          const subComingSoon = subitem.comingSoon;
                          return (
                            <button
                              key={subitem.path}
                              onClick={() => {
                                if (subComingSoon) return; // Disable click for coming soon
                                navigate(subitem.path);
                                setSidebarOpen(false);
                              }}
                              title={subComingSoon ? "Coming Soon" : ""}
                              className={`
                                w-full text-left px-4 py-2 rounded-lg text-sm
                                transition-colors flex items-center justify-between
                                ${subComingSoon
                                  ? 'opacity-40 cursor-not-allowed'
                                  : isActive(subitem.path)
                                    ? 'bg-blue-700 text-white'
                                    : 'text-blue-200 hover:bg-blue-800/50'
                                }
                              `}
                            >
                              <span>{subitem.label}</span>
                              {subComingSoon && (
                                <span className="text-[8px] bg-yellow-500 text-white px-1.5 py-0.5 rounded-full font-semibold">
                                  SOON
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-blue-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg
                         bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:overflow-hidden min-w-0">
        <BetaBanner />
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
            >
              <Menu size={24} />
            </button>
            
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search tenants or rooms..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button 
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Notifications"
            >
              <Bell size={22} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </button>
            
            {/* Property Selector Dropdown */}
            <div className="relative property-dropdown-container">
              <button 
                onClick={() => setShowPropertyDropdown(!showPropertyDropdown)}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-4 py-2 rounded-lg text-white shadow-md hover:shadow-lg transition-all"
                title="Filter Properties"
              >
                <Building2 size={18} />
                <span className="text-sm font-medium">
                  {selectedProperties.length === properties.length 
                    ? `All Properties (${properties.length})`
                    : `${selectedProperties.length} Selected`}
                </span>
                <ChevronDown size={16} className={`transition-transform ${showPropertyDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showPropertyDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 text-sm">Filter Properties</h3>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                        {selectedProperties.length}/{properties.length}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllProperties}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-gray-300">•</span>
                      <button
                        onClick={clearAllSelections}
                        className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  
                  {properties.length > 0 ? (
                    <div className="py-2">
                      {properties.map((property) => {
                        const isSelected = selectedProperties.includes(property.id);
                        return (
                          <button
                            key={property.id}
                            onClick={() => togglePropertySelection(property.id)}
                            className={`w-full px-4 py-3 transition-colors text-left group ${
                              isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-blue-600 border-blue-600' 
                                  : 'border-gray-300 group-hover:border-blue-400'
                              }`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>

                              {/* Property Icon */}
                              <div className={`p-2 rounded-lg transition-colors ${
                                isSelected ? 'bg-blue-200' : 'bg-gray-100 group-hover:bg-blue-100'
                              }`}>
                                <Building2 size={18} className={isSelected ? 'text-blue-700' : 'text-gray-600'} />
                              </div>

                              {/* Property Details */}
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm truncate ${
                                  isSelected ? 'text-blue-900' : 'text-gray-900'
                                }`}>
                                  {property.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate mt-1">{property.address}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs">
                                  <span className="text-gray-600">
                                    <span className="font-medium">{property.total_rooms}</span> rooms
                                  </span>
                                  <span className="text-green-600">
                                    <span className="font-medium">{property.occupied_beds || 0}</span> occupied
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Building2 size={48} className="mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500 text-sm mb-3">No properties yet</p>
                      <button
                        onClick={() => {
                          navigate('/manager');
                          setShowPropertyDropdown(false);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Go to Dashboard →
                      </button>
                    </div>
                  )}

                  {properties.length > 0 && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <button
                        onClick={() => {
                          setShowPropertyDropdown(false);
                        }}
                        className="w-full text-center text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 py-2 rounded-lg transition-all"
                      >
                        Apply Filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content — on mobile, let the body scroll naturally so iOS
            Safari's address bar can collapse for extra height. On desktop,
            keep the inner-overflow pattern so the sidebar stays sticky. */}
        <main className="flex-1 lg:overflow-y-auto bg-gray-50">
          <Outlet context={{ selectedProperties, properties }} />
        </main>
      </div>

      {/* Mobile-only bottom navigation. Renders nothing on lg+ */}
      <MobileBottomNav />

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* First-time onboarding wizard — shows when user has 0 properties */}
      <OnboardingWizard
        open={propsLoaded && properties.length === 0 && !onboardingDone}
        onComplete={refreshPropertiesAfterOnboarding}
        token={localStorage.getItem('token')}
        userName={profileName}
      />
    </div>
  );
};

export default ManagerLayout;
